import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import AuthModal from '@/components/AuthModal';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { Menu, X, ChevronDown, ChevronUp, GraduationCap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQPage = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [branding, setBranding] = useState({ logo_url: null, site_name: 'Train In Japan' });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  useEffect(() => {
    fetchBranding();
  }, []);

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/branding/logo`);
      setBranding(response.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
    }
  };

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const faqCategories = [
    {
      title: 'About Train In Japan',
      faqs: [
        {
          question: 'What is Train In Japan?',
          answer: 'Train In Japan is your dedicated booking platform for discovering and reserving authentic martial arts and traditional cultural training experiences in Japan. We connect you directly with vetted, high-quality dojos and instructors, ensuring a genuine and transformative journey.'
        },
        {
          question: 'How is Train In Japan different from general booking sites?',
          answer: 'We specialize exclusively in immersive training experiences, not general tourism. We focus on connecting you with real masters and traditional environments, handling the unique logistics of martial arts and cultural study, and providing dedicated support throughout your learning adventure.'
        },
        {
          question: 'What types of courses do you offer?',
          answer: 'We currently offer a curated selection of courses in various traditional Japanese martial arts (e.g., Shotokan Karate, Aikido, Kyudo, Daito-ryu, Muso Jikiden Eishin-ryu, Asayama Ichiden-ryu) and cultural arts (e.g., Taiko drumming, Sumi-e ink painting, tea ceremony, calligraphy).'
        }
      ]
    },
    {
      title: 'Booking Your Course',
      faqs: [
        {
          question: 'What is the booking process like?',
          answer: "Once you find a course, you can submit an inquiry or deposit and book through our secure platform. You'll typically provide some basic information about your experience level and goals. Our team will then liaise with the school to confirm availability and suitability."
        },
        {
          question: 'Do I need prior experience in a specific art to join a course?',
          answer: 'Many beginner-level courses require no prior experience. However, some advanced programs or "Black Belt Up" intensives will have specific prerequisites (e.g., current belt rank). These will be clearly stated in the course description.'
        }
      ]
    },
    {
      title: "What's Included & Excluded",
      faqs: [
        {
          question: "What's typically included in a Train In Japan course booking?",
          answer: "Your booking typically includes the stated training hours/sessions with the specific school/instructor. Additionally, you'll receive a training uniform (gi/dogi) suitable for your chosen art, and membership to the association or organization you train with."
        },
        {
          question: 'Does membership to an association offer any additional benefits?',
          answer: 'Yes! Membership often grants you official recognition within that specific martial arts association or cultural organization. For many martial arts, this means your progress is formally recorded. Some associations also have international schools or dojos, which you may be able to visit or train at in the future.'
        },
        {
          question: 'Is international airfare, accommodation, or food included?',
          answer: 'Generally, no. Our core service connects you with the training. Most courses do not include international airfare, local transportation, accommodation, or meals, unless explicitly stated otherwise in the specific course description. We may offer guidance or recommendations for these aspects.'
        },
        {
          question: 'Do you help with visas or travel insurance?',
          answer: "We provide guidance and resources regarding visa requirements for your nationality and country of origin. We strongly recommend all students obtain comprehensive travel insurance. While we offer information, securing these is ultimately the student's responsibility."
        }
      ]
    },
    {
      title: 'Preparation & On-the-Ground Experience',
      faqs: [
        {
          question: 'What should I do to prepare for my trip?',
          answer: "We will provide a comprehensive pre-departure orientation, including cultural etiquette tips, packing advice, and practical information about daily life in Japan. We recommend researching the specific region you're visiting and getting in good physical condition for intensive training."
        },
        {
          question: 'What is dojo etiquette like in Japan?',
          answer: 'Japanese dojos (training halls) have specific etiquette (reigi) rooted in tradition and respect. We will provide detailed guidelines in your pre-departure materials to ensure you feel comfortable and respectful in your training environment.'
        },
        {
          question: 'Will there be a language barrier?',
          answer: 'Our partner schools are accustomed to international students. While some instructors may have English proficiency, much of the teaching may be demonstrated. We encourage learning basic Japanese phrases, and some courses may include language support. Also, we can help you arrange a translator where required.'
        },
        {
          question: "What if I need help while I'm in Japan?",
          answer: 'Train In Japan provides 24/7 emergency contact support. We also work to establish local support networks in Japan to assist you with any unforeseen issues or challenges that may arise during your stay.'
        }
      ]
    },
    {
      title: 'Safety, Protection & Quality',
      faqs: [
        {
          question: 'How do you ensure the quality of the schools and instructors?',
          answer: 'We implement a rigorous vetting process for all partner schools, including documentation review, curriculum assessment, and optional Zoom interviews.'
        },
        {
          question: 'How are students protected?',
          answer: 'We have a clear Code of Conduct for all participants. We also conduct basic student vetting to ensure suitability for the programs and foster a safe learning environment. A robust feedback and complaint resolution process is in place for any concerns.'
        },
        {
          question: 'What if I need to cancel my course?',
          answer: 'Cancellation policies vary by course and are clearly outlined in the booking terms and conditions for each program. Please review these carefully before booking.'
        }
      ]
    },
    {
      title: 'After Your Course',
      faqs: [
        {
          question: 'What happens after my course concludes?',
          answer: "We encourage you to provide feedback on your experience. You'll also become part of the Train In Japan alumni community, where you can connect with other passionate learners and share your ongoing journey."
        },
        {
          question: 'Can I book multiple courses or return for another program?',
          answer: 'Absolutely! Many students find their first experience so rewarding that they wish to explore more. Our platform makes it easy to book future courses and continue your learning journey in Japan.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 via-emerald-50 to-slate-100">
      {/* Floating Logo - Detached from header */}
      <div className="fixed top-2 sm:top-3 left-3 sm:left-4 md:left-6 z-[60] cursor-pointer" onClick={() => navigate('/')}>
        <div 
          className="flex flex-col items-center"
          style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))' }}
        >
          {branding.logo_url && (
            <div className="w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-white border-2 sm:border-[3px] border-emerald-600 overflow-hidden">
              <img src={branding.logo_url} alt="Logo" className="w-full h-full object-cover" />
            </div>
          )}
          <span 
            className="mt-1 text-xs sm:text-sm md:text-base font-bold text-slate-800 bg-white/90 px-2 py-0.5 rounded-full backdrop-blur-sm"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            {branding.site_name}
          </span>
        </div>
      </div>

      {/* Navigation Bar - Slim header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-2 flex items-center justify-between">
          {/* Spacer for center logo */}
          <div className="w-8 sm:w-12"></div>
          
          {/* Desktop Nav - Right side */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6">
            <button onClick={() => navigate("/faq")} className="text-slate-700 hover:text-emerald-700 font-medium transition-colors">{t("nav.faq")}</button>
            <Button 
              data-testid="nav-schools" 
              onClick={() => navigate('/register-school')} 
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-6 py-2 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              {t('nav.forSchools')}
            </Button>
            <div className="flex items-center gap-2">
              {user ? (
                <Button data-testid="nav-dashboard-btn" onClick={() => navigate('/dashboard')} size="sm" className="bg-emerald-700 hover:bg-emerald-800">{t('nav.dashboard')}</Button>
              ) : (
                <Button data-testid="nav-login-btn" onClick={() => setAuthModalOpen(true)} size="sm" className="bg-emerald-700 hover:bg-emerald-800">{t('nav.signIn')}</Button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
          
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 px-4 py-3 space-y-2">
            <button onClick={() => { navigate("/faq"); setMobileMenuOpen(false); }} className="block w-full text-left py-2 text-slate-700 font-medium">{t("nav.faq")}</button>
            <Button 
              onClick={() => { navigate('/register-school'); setMobileMenuOpen(false); }} 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-2 rounded-lg shadow-md flex items-center justify-center gap-2"
            >
              <GraduationCap className="w-5 h-5" />
              {t('nav.forSchools')}
            </Button>
            <div className="flex items-center gap-2 pt-1">
              {user ? (
                <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} size="sm" className="flex-1 bg-emerald-700">{t('nav.dashboard')}</Button>
              ) : (
                <Button onClick={() => { setAuthModalOpen(true); setMobileMenuOpen(false); }} size="sm" className="flex-1 bg-emerald-700">{t('nav.signIn')}</Button>
              )}
              <LanguageSwitcher />
            </div>
          </div>
        )}
      </nav>

      {/* FAQ Header */}
      <section className="pt-24 sm:pt-28 md:pt-32 pb-12 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600" style={{ fontFamily: 'Spectral, serif' }}>
            Your Journey to Authentic Training in Japan
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          {faqCategories.map((category, catIdx) => (
            <div key={catIdx} className="mb-10">
              <h2 className="text-2xl font-bold text-emerald-800 mb-6 pb-2 border-b-2 border-emerald-200" style={{ fontFamily: 'Playfair Display, serif' }}>
                {category.title}
              </h2>
              <div className="space-y-3">
                {category.faqs.map((faq, faqIdx) => {
                  const globalIdx = `${catIdx}-${faqIdx}`;
                  const isOpen = openFAQ === globalIdx;
                  return (
                    <div 
                      key={faqIdx} 
                      className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200 hover:shadow-lg transition-shadow"
                    >
                      <button
                        onClick={() => toggleFAQ(globalIdx)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-semibold text-slate-800 pr-4" style={{ fontFamily: 'Spectral, serif' }}>
                          {faq.question}
                        </span>
                        {isOpen ? (
                          <ChevronUp className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4 pt-2 bg-emerald-50/50">
                          <p className="text-slate-600 leading-relaxed">
                            {faq.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 bg-gradient-to-r from-emerald-700 to-emerald-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Begin Your Journey?
          </h2>
          <p className="text-emerald-100 mb-8">
            Explore our programs and find the perfect training experience for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/programs')} 
              size="lg" 
              className="bg-white text-emerald-800 hover:bg-emerald-50"
            >
              Browse Programs
            </Button>
            <Button 
              onClick={() => navigate('/')} 
              size="lg" 
              variant="outline"
              className="border-white text-white hover:bg-white/10"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t('footer.title')}</h3>
          <p className="text-slate-400">{t('footer.subtitle')}</p>
          <p className="text-slate-500 mt-8 text-sm">{t('footer.copyright')}</p>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        open={authModalOpen} 
        onClose={() => setAuthModalOpen(false)} 
      />
    </div>
  );
};

export default FAQPage;
